import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Reveal } from "@/components/Motion";
import { money, api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { FRAG_AUCTION, FRAG_PRICED, FRAG_GALLERY } from "@/lib/frags";
import { Gavel, ArrowRight, Phone, X, Flame } from "lucide-react";

export default function Wysiwyg() {
  const [lightbox, setLightbox] = useState(null);
  const [buyables, setBuyables] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    api.get("/products").then((r) => setBuyables(r.data.filter((p) => p.wysiwyg))).catch(() => {});
  }, []);

  const onAdd = (p) => { add(p); toast.success(`${p.name} added to cart`); };

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-36 pb-28 min-h-screen" data-testid="wysiwyg-page">
      <Reveal>
        <span className="mono-label text-cyan">WYSIWYG · What You See Is What You Get</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 mb-4 max-w-3xl">
          Fresh Cut <span className="italic text-cyan">Frags</span>
        </h1>
        <p className="text-white/55 max-w-2xl leading-relaxed">
          Just fragged and ready to go — every coral below is the exact piece you'll receive. Grab them at the listed
          price, or win them in our live auction. Local pickup in South Florida or safe overnight shipping.
        </p>
      </Reveal>

      {/* VIDEO */}
      <Reveal delay={0.05}>
        <div className="relative mt-10 rounded-3xl overflow-hidden border border-white/10" data-testid="wysiwyg-video-wrap">
          <video
            data-testid="wysiwyg-video"
            className="w-full max-h-[520px] object-cover bg-black"
            src="/corals/fresh-frags-video.mp4"
            autoPlay muted loop playsInline controls
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-void/80 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 md:p-8">
            <span className="mono-label text-reefpink flex items-center gap-2"><Flame size={14} /> Fresh cut · Available now</span>
            <h2 className="font-display text-2xl md:text-3xl mt-2">New coral frags — for sale &amp; live auction</h2>
          </div>
        </div>
      </Reveal>

      {/* AUCTION BANNER */}
      <Reveal delay={0.1}>
        <div className="mt-8 glass rounded-2xl border border-reefpink/30 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between"
          style={{ boxShadow: "0 30px 90px -40px rgba(255,0,127,0.4)" }} data-testid="wysiwyg-auction-banner">
          <div>
            <span className="mono-label text-reefpink flex items-center gap-2"><Gavel size={14} /> Live frag auction</span>
            <h3 className="font-display text-2xl md:text-3xl mt-2">Bidding starts at {money(FRAG_AUCTION.startBid)}</h3>
            <p className="text-white/55 text-sm mt-2">
              Starts <b className="text-white/80">{FRAG_AUCTION.startsLabel}</b> · Ends <b className="text-white/80">{FRAG_AUCTION.endsLabel}</b>
            </p>
          </div>
          <Link to="/auctions" data-testid="wysiwyg-bid-btn" className="btn-pill btn-pink flex items-center gap-2 shrink-0">
            Bid now <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>

      {/* BUY IT NOW / PRICE LIST */}
      <Reveal delay={0.05}>
        <div className="mt-20">
          <span className="mono-label text-cyan">Buy it now · WYSIWYG</span>
          <h2 className="font-display text-3xl md:text-4xl mt-3 mb-6">Grab it at the listed price</h2>
        </div>
      </Reveal>

      {buyables.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="wysiwyg-buy-grid">
          {buyables.map((p, i) => (
            <ProductCard key={p.id} product={p} onAdd={onAdd} index={i} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 overflow-hidden max-w-2xl" data-testid="wysiwyg-price-list">
          {FRAG_PRICED.map((f, i) => (
            <div key={i} data-testid={`frag-priced-${i}`}
              className="grid grid-cols-[1fr_auto] items-center gap-4 px-5 py-3.5 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
              <div className="min-w-0">
                <span className="text-white/85 text-sm md:text-base">{f.name}</span>
                {f.code && <span className="ml-2 mono-label text-[0.6rem] text-white/35">{f.code}</span>}
              </div>
              <span className="font-mono text-cyan text-sm md:text-base">{money(f.price)}</span>
            </div>
          ))}
          <div className="px-5 py-4 bg-white/[0.03]">
            <Link to="/auctions" className="text-reefpink text-sm hover:underline flex items-center gap-1.5">
              Prefer to bid? Every frag also goes live in the auction <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      )}

      {/* AVAILABLE FRAG TRAYS */}
      <Reveal delay={0.05}>
        <div className="mt-24">
          <span className="mono-label text-cyan">More available now</span>
          <h2 className="font-display text-3xl md:text-4xl mt-3 mb-8">Fresh frag trays</h2>
        </div>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wysiwyg-gallery">
        {FRAG_GALLERY.map((g, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <button onClick={() => setLightbox(g.img)} data-testid={`frag-tray-${i}`}
              className="group relative w-full text-left rounded-2xl overflow-hidden border border-white/10 card-glow">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={g.img} alt={g.title} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1.1s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-void via-void/10 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 p-4">
                <h3 className="font-display text-lg leading-tight">{g.title}</h3>
                <p className="text-white/55 text-xs mt-1">{g.note}</p>
              </div>
            </button>
          </Reveal>
        ))}
      </div>

      {/* CTA */}
      <Reveal delay={0.05}>
        <div className="mt-20 glass rounded-3xl border border-white/10 p-8 md:p-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl mb-3">Want a frag? Let's talk.</h2>
          <p className="text-white/55 max-w-xl mx-auto mb-7">
            Message us to claim any coral at the listed price, ask for a wholesale quote, or get in on the live auction.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/auctions" className="btn-pill btn-solid flex items-center gap-2" data-testid="wysiwyg-cta-auction">
              <Gavel size={16} /> Go to auctions
            </Link>
            <a href="tel:+19548680608" className="btn-pill border border-white/15 text-white/80 hover:text-cyan flex items-center gap-2" data-testid="wysiwyg-cta-call">
              <Phone size={16} /> Call / text 954-868-0608
            </a>
          </div>
        </div>
      </Reveal>

      {/* LIGHTBOX */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} data-testid="wysiwyg-lightbox"
          className="fixed inset-0 z-[100] bg-void/95 backdrop-blur flex items-center justify-center p-4">
          <button className="absolute top-5 right-5 text-white/70 hover:text-white" data-testid="wysiwyg-lightbox-close">
            <X size={28} />
          </button>
          <img src={lightbox} alt="Coral frag" className="max-w-full max-h-[90vh] object-contain rounded-xl" />
        </div>
      )}
    </div>
  );
}
